import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import DeliveryTypeService from '../../../../../services/orders/deliveryType';

// props
import { IDeliveryTypeResourceShortProps } from '../../../../../props/orders/deliveryType';

// components
import { ActionButton, CommandBar, IColumn, ICommandBarItemProps, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import Text from '../../../../typography/text';
import DeliveryTypeForm from '../form';

interface IDeliveryTypesListProps {
}

const DeliveryTypesList: React.FC<IDeliveryTypesListProps> = (props: IDeliveryTypesListProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [deliveryTypes, setDeliveryTypes] = React.useState<IDeliveryTypeResourceShortProps[]>([]);
    const [activeDeliveryType, setActiveDeliveryType] = React.useState<IDeliveryTypeResourceShortProps | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();

    const columns: IColumn[] = [
        {
            key: "uniqueKey",
            fieldName: "key",
            name: "Unique Key",
            minWidth: 120,
            maxWidth: 120,
            onRender: (item: IDeliveryTypeResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}} verticalAlign={"center"}>
                    <Text>{item.key}</Text>
                </Stack>;
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 50,
            maxWidth: 50,
            onRender: (item: IDeliveryTypeResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}} verticalAlign={"center"}>
                    <Stack.Item className={"detailsListActionRow"}>
                        <ActionButton className={'detailsListActionButton'} menuProps={{
                            items: [
                                {key: "update", text: "Update", iconProps: {iconName: "Edit"}, onClick: () => {
                                    setActiveDeliveryType(item);
                                    setActiveSurface('update');
                                }}
                            ]
                        }} />
                    </Stack.Item>
                </Stack>;
            }
        },
        {
            key: "name",
            fieldName: "name",
            name: "Name",
            minWidth: 0,
            onRender: (item: IDeliveryTypeResourceShortProps) => {
                return <Stack styles={{root: {height: '100%'}}} verticalAlign={"center"}>
                    <Text>{item.name}</Text>
                </Stack>;
            }
        },
    ];

    useEffect(() => {
        _onRetrieveDeliveryTypes();
    }, []);

    const _onRetrieveDeliveryTypes = async (pageNumber?: number, ) => {
        try {
            if (!pageNumber) {
                setLoaded(false);
            }

            const qs: string[] = [];
            const results = await DeliveryTypeService.retrieve();
            setDeliveryTypes(results);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_delivery_types_error',
                text: 'Failed to retrieve delivery types. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?:string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        items.push({
            key: "search",
            onRender: () => {
                return <SearchBox placeholder={"Search ..."} onSearch={_onKeywordChanged} />
            }
        });

        farItems.push({
            key: "register",
            text: "Create Delivery Type",
            iconProps: { iconName: "Add" },
            onRender: () => { 
                return <PrimaryButton text={"Create Delivery Type"} 
                    iconProps={{ iconName: "Add" }} 
                    onClick={() => {setActiveSurface('create') }} 
                    styles={{ root: { marginLeft: 10 } }} />;
            }
        });

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveDeliveryType(undefined);

        if (refresh) {_onRetrieveDeliveryTypes()}
    }

    return <Stack className={styles.container} tokens={{childrenGap: 20}}>
        <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{root: {
                padding: 0,
                height: 'unset',
                backgroundColor: 'transparent'
            }}}
            ariaLabel="Use left and right arrow keys to navigate between commands" />
        <Stack className={styles.list}>
            {
                !loaded || (loaded && deliveryTypes.length > 0) ? (
                    <>
                        <ShimmeredDetailsList
                            setKey="items"
                            items={deliveryTypes}
                            columns={columns}
                            selectionMode={SelectionMode.none}
                            enableShimmer={!loaded}
                            onShouldVirtualize={() => false}
                            ariaLabelForShimmer="Content is being fetched"
                            ariaLabelForGrid="Item details" />
                    </>
                ) : null
            }
            {loaded && deliveryTypes.length < 1 ? <Stack styles={{root: {padding: 10}}}>
                <Text>Delivery type(s) not found</Text>
            </Stack> : null}
        </Stack>
        {activeSurface === 'create' ? <DeliveryTypeForm onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'update' && activeDeliveryType ? <DeliveryTypeForm deliveryType={activeDeliveryType} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default DeliveryTypesList;
